import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu'
import { MatSelectChange, MatSelectModule } from '@angular/material/select'
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserService } from '../../../shared/services/user.service';

@Component({
  selector: 'app-tool-bar',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatToolbarModule, MatMenuModule, MatSelectModule],
  templateUrl: './tool-bar.component.html',
  styleUrl: './tool-bar.component.scss'
})
export class ToolBarComponent {
  @Output() toggleSidenav = new EventEmitter<void>();
  @Output() toggleRightSidenav = new EventEmitter<void>();
  @Output() loginRedirect = new EventEmitter<void>();
  isOutage = true; // Set this dynamically based on your API response or logic

  loginDisplay = false;
  username: string | undefined;
  userRoles: { groupName: string; groupId: string; role: string }[] = [];
  constructor(private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private http: HttpClient,
    private userService: UserService) { }
  ngOnInit(): void {
    setTimeout(() => {
      this.isOutage = false;
    }, 10000); // Hide after 5 seconds
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
      )
      .subscribe((result: EventMessage) => {
        // console.log(result);
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        this.setLoginDisplay();
      })

  }

  userData: any;

  async setLoginDisplay() {
    let account = this.authService.instance.getActiveAccount();

    if (account != null && account.idToken && account.idTokenClaims) {
      const displayName = account.name ? account.name.slice(0, -4) : "Unknown User"; // Trim last 4 characters
      const email = account.username || "No Email";
      const userId = account.localAccountId;

      let groupIds =  await this.getEffectiveGroupIds(account);

      console.log(groupIds);
      // Update UI or store user details
      this.updateUserData(displayName, email, userId, groupIds);
    }
  }

  async fetchUserGroups(): Promise<string[]> {
    try {
      const token = await this.getAccessToken();
      const account = this.authService.instance.getActiveAccount();

      if (!account) {
        throw new Error("No active account found.");
      }

      const userId = account.localAccountId;
      const apiUrl = `https://graph.microsoft.com/v1.0/users/${userId}/getMemberObjects`;

      const headers = new HttpHeaders({
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      });

      // Convert Observable to Promise using firstValueFrom() (recommended)
      const response = await firstValueFrom(this.http.post<{ value: string[] }>(apiUrl, { securityEnabledOnly: false }, { headers }));

      return response?.value || []; // Return an empty array if no groups found
    } catch (error) {
      console.error("Error fetching groups:", error);
      return []; // Ensure function always returns an array
    }
  }

  // Merge token claims + Graph groups and return unique IDs/names
  async getEffectiveGroupIds(account: any): Promise<string[]> {
    // Pull both common claim types from the token (some tenants use "groups", others "roles")
    const tokenGroups: string[] = Array.isArray(account?.idTokenClaims?.groups)
      ? account.idTokenClaims.groups as string[]
      : [];

    const tokenRoles: string[] = Array.isArray(account?.idTokenClaims?.roles)
      ? account.idTokenClaims.roles as string[]
      : [];

    // Always try Graph as well (comment out if you only want it as a fallback)
    let graphGroups: string[] = [];
    try {
      graphGroups = await this.fetchUserGroups(); // should return string[] of group IDs
    } catch (error) {
      console.error("Error fetching groups:", error);
    }

    // Normalize, dedupe, and drop empties
    const normalize = (s: unknown) => String(s ?? "").trim();
    const distinct = new Set<string>();

    for (const v of [...tokenGroups, ...tokenRoles, ...graphGroups]) {
      const n = normalize(v);
      if (n) distinct.add(n);
    }

    return [...distinct];
  }



  async getAccessToken(): Promise<string> {
    const account = this.authService.instance.getActiveAccount();
    if (!account) {
      throw new Error("No active account found. Please login first.");
    }

    const tokenRequest = {
      scopes: ["https://graph.microsoft.com/.default"], // ✅ Ensures correct audience
      account: account
    };

    try {
      const response: AuthenticationResult = await this.authService.instance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error) {
      console.error("Error acquiring token:", error);
      throw error;
    }
  }

  updateUserData(displayName: string, email: string, userId: string, groupIds: string[]) {
    // Map group IDs to roles
    const userGroups = groupIds
      .map((groupId: string) => {
        const role = groupRoleMap[groupId] || null;
        return role ? { groupName: null, groupId, role } : null;
      })
      .filter(group => group !== null) as { groupName: string | null; groupId: string; role: string }[];

    // Extract roles from the mapped groups
    const userRoles = userGroups.map(g => g.role);

    // Determine the highest role
    const currentRole = this.getHighestRole(userRoles);

    // Construct the User object
    this.userData = {
      displayName,
      email,
      userId,
      groups: userGroups,
      roles: userRoles,
      currentRole
    };

    this.userService.setUserData(this.userData);
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;

    // console.log("User Data:", this.userData);
  }


  getHighestRole(userRoles: string[]): string {
    const roleOrder = ["GlobalAdmin", "PortfolioCoordinator", "Appraiser", "User", "GlobalReader", "Reader"];

    for (let role of roleOrder) {
      if (userRoles.includes(role)) {
        return role; // Return the highest role found
      }
    }
    return "User"; // Default to User if no role is found
  }

  Login() {
    this.loginRedirect.emit();
  }

  onToggleSidenav() {
    this.toggleSidenav.emit();
  }

  onToggleRightSidenav() {
    this.toggleRightSidenav.emit();
  }

  onRoleChange(event: MatSelectChange) {
    this.userData.currentRole = event.value;
    this.userService.setUserData(this.userData);
    // Perform any necessary actions, like updating permissions or refreshing data
  }
}

export const groupRoleMap: { [key: string]: string } = {
  "136c286a-6d25-4332-8433-8f51b3c1c5b8": "Appraiser",
  "c514850a-ae7f-4c60-a41c-2ff5787d46ca": "Reader",
  "137878c4-e3f7-47c4-9070-84b046567ca7": "GlobalAdmin",
  "5729de64-a53a-48b5-a7a9-8949acc93a4e": "GlobalReader",
  "b17c89fe-c84d-40b5-8b84-dc7a2f758758": "User",
  "d3c6b668-34a9-42af-9b2a-e124e216bc9c": "PortfolioCoordinator",
  "120d0a50-26a8-463c-9b54-f55bea83efa8": "Ratings"
};
const roleHierarchy: { [key: string]: string[] } = {
  "GlobalAdmin": ["PortfolioCoordinator", "Appraiser", "Ratings", "User", "GlobalReader", "Reader"],
  "PortfolioCoordinator": ["Appraiser", "Ratings", "User", "GlobalReader", "Reader"],
  "Appraiser": ["Ratings", "User"],
  "Ratings": [],
  "User": [],
  "GlobalReader": ["Reader"],
  "Reader": []
};
